
import React, { useState } from 'react';
import { ProductConfig, ComponentOption, CompanyInfo, ColorOption, SizeOption } from '../types';
import { INITIAL_CONFIG } from '../constants';
import { supabase } from '../supabase';

type TabType = 'dashboard' | 'sizes' | 'headboards' | 'bases' | 'mattresses' | 'toppers' | 'poets' | 'voetbords' | 'voets' | 'nightstands' | 'colors' | 'company';

interface AdminPanelProps {
  configs: ProductConfig[];
  activeId: string;
  companyInfo: CompanyInfo;
  onUpdateCompanyInfo: (info: CompanyInfo) => void;
  onUpdateConfigs: React.Dispatch<React.SetStateAction<ProductConfig[]>>;
  onSetActiveId: (id: string) => void;
  onClose: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ 
  configs, 
  activeId, 
  companyInfo, 
  onUpdateCompanyInfo, 
  onUpdateConfigs, 
  onSetActiveId, 
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const currentConfig = configs.find(c => c.id === activeId) || configs[0] || INITIAL_CONFIG;

  /**
   * Recurse through an object and remove all empty strings, nulls, or undefined fields.
   * This saves significant space in the database and local cache.
   */
  const deepClean = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(v => deepClean(v)).filter(v => v !== null && v !== undefined && (typeof v !== 'string' || v !== ''));
    } else if (obj !== null && typeof obj === 'object') {
      return Object.entries(obj).reduce((acc, [k, v]) => {
        const cleaned = deepClean(v);
        if (cleaned !== null && cleaned !== undefined && (typeof cleaned !== 'string' || cleaned !== '')) {
          acc[k] = cleaned;
        }
        return acc;
      }, {} as any);
    }
    return obj;
  };

  const handleSaveToCloud = async () => {
    try {
      setIsSaving(true);
      // Data optimization by removing empty fields
      const cleanedConfigs = deepClean(configs);
      const cleanedCompanyInfo = deepClean(companyInfo);

      const payload = { 
        id: 'main', 
        configs: cleanedConfigs, 
        company_info: cleanedCompanyInfo,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('settings')
        .upsert(payload, { onConflict: 'id' });

      if (error) throw error;
      
      setHasUnsavedChanges(false);
      alert("Succesvol opgeslagen en geoptimaliseerd!");
    } catch (e: any) {
      alert("Fout bij opslaan: " + (e.message || "Onbekende fout"));
    } finally {
      setIsSaving(false);
    }
  };

  const safeUpdate = (updater: (prev: ProductConfig[]) => ProductConfig[]) => {
    onUpdateConfigs(prev => {
      const next = updater([...prev]);
      setHasUnsavedChanges(true);
      return next;
    });
  };

  const moveItem = (listKey: keyof ProductConfig, index: number, direction: 'up' | 'down') => {
    safeUpdate(prev => prev.map(config => {
      if (config.id === currentConfig.id) {
        const list = [...(config[listKey] as any[])];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex >= 0 && targetIndex < list.length) {
          [list[index], list[targetIndex]] = [list[targetIndex], list[index]];
        }
        return { ...config, [listKey]: list };
      }
      return config;
    }));
  };

  const compressImage = (file: File, isLogo: boolean = false): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = isLogo ? 400 : 1000;
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
          } else {
            if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/webp', 0.8));
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleFileUpload = async (listKey: keyof ProductConfig | 'company' | 'noneTopper', itemId: string, field: string, file: File, variantId?: string) => {
    try {
      setIsCompressing(true);
      const isLogo = field === 'logoUrl';
      const compressedBase64 = await compressImage(file, isLogo);
      
      if (listKey === 'company') {
        onUpdateCompanyInfo({ ...companyInfo, [field]: compressedBase64 });
        setHasUnsavedChanges(true);
      } else if (listKey === 'noneTopper') {
        safeUpdate(prev => prev.map(c => c.id === currentConfig.id ? { ...c, [field]: compressedBase64 } : c));
      } else {
        handleUpdateField(listKey as any, itemId, field, compressedBase64, variantId);
      }
    } catch (error) {
      alert("Fout bij verwerken van afbeelding.");
    } finally {
      setIsCompressing(false);
    }
  };

  const handleUpdateField = (listKey: keyof ProductConfig, itemId: string, field: string, value: any, variantId?: string) => {
    safeUpdate(prev => prev.map(config => {
      if (config.id === currentConfig.id) {
        const list = config[listKey] as any[];
        const newList = list.map(item => {
          if (item.id === itemId) {
            if (variantId && item.variants) {
              const newVariants = item.variants.map((v: any) => v.id === variantId ? { ...v, [field]: value } : v);
              return { ...item, variants: newVariants };
            }
            return { ...item, [field]: value };
          }
          return item;
        });
        return { ...config, [listKey]: newList };
      }
      return config;
    }));
  };

  const handleAddSubItem = (listKey: keyof ProductConfig) => {
    const newId = `item-${Date.now()}`;
    safeUpdate(prev => prev.map(config => {
      if (config.id === currentConfig.id) {
        let newItem: any;
        if (listKey === 'sizes') {
          newItem = { id: newId, label: '100x200', width: 100, length: 200, priceModifier: 0 };
        } else if (listKey === 'colors') {
          newItem = { id: newId, name: 'Nieuwe Kleur', hex: '#cccccc', fabricType: 'Stof', priceModifier: 0 };
        } else {
          newItem = { id: newId, name: 'Nieuw Model', priceModifier: 0, imageUrl: '', imageUrlFront: '' };
          if (listKey === 'headboards') {
            newItem.variants = [
              { id: 'v-r-' + newId, label: 'R', priceModifier: 0, imageUrl: '', imageUrlFront: '' },
              { id: 'v-m-' + newId, label: 'M', priceModifier: 0, imageUrl: '', imageUrlFront: '' },
              { id: 'v-xl-' + newId, label: 'XL', priceModifier: 150, imageUrl: '', imageUrlFront: '' },
              { id: 'v-xxl-' + newId, label: 'XXL', priceModifier: 250, imageUrl: '', imageUrlFront: '' },
            ];
          }
        }
        // Place new items at the top
        return { ...config, [listKey]: [newItem, ...(config[listKey] as any[])] };
      }
      return config;
    }));
  };

  const handleRemoveSubItem = (listKey: keyof ProductConfig, itemId: string) => {
    if (confirmDeleteId !== itemId) {
      setConfirmDeleteId(itemId);
      return;
    }
    safeUpdate(prev => prev.map(config => {
      if (config.id === currentConfig.id) {
        const list = config[listKey] as any[];
        return { ...config, [listKey]: list.filter(item => item.id !== itemId) };
      }
      return config;
    }));
    setConfirmDeleteId(null);
  };

  const NavButton = ({ tab, label, icon }: { tab: TabType, label: string, icon: string }) => (
    <button onClick={() => setActiveTab(tab)} className={`w-full text-left px-3 py-2.5 rounded-xl text-[9.5px] font-bold uppercase tracking-wider transition-all flex items-center gap-3 ${activeTab === tab ? 'bg-white text-black shadow-lg' : 'text-zinc-500 hover:text-white hover:bg-white/5'}`}>
      <span className="text-base">{icon}</span> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row font-sans text-black" onClick={() => setConfirmDeleteId(null)}>
      {(isCompressing || isSaving) && (
        <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-3 border-black border-t-transparent rounded-full animate-spin"></div>
            <p className="text-[10px] font-bold uppercase tracking-widest">{isSaving ? 'Opslaan...' : 'Verwerken...'}</p>
          </div>
        </div>
      )}
      
      <aside className="lg:w-[220px] bg-[#0a0a0a] text-white p-5 lg:fixed lg:inset-y-0 z-[100] flex flex-col border-r border-zinc-900 overflow-y-auto">
        <div className="flex items-center gap-3 mb-6 px-1">
          <div className="w-8 h-8 bg-white text-black flex items-center justify-center font-black rounded-lg text-lg">B</div>
          <h1 className="text-[10px] font-black uppercase tracking-[0.2em]">ADMIN</h1>
        </div>

        <div className="mb-6">
          <button 
            onClick={handleSaveToCloud} 
            className={`w-full py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${hasUnsavedChanges ? 'bg-amber-500 hover:bg-amber-600' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'}`}
          >
            {isSaving ? 'Bezig...' : 'Wolk Opslaan'}
          </button>
        </div>
        
        <div className="flex-1 space-y-1">
          <NavButton tab="dashboard" label="DASHBOARD" icon="📂" />
          <div className="py-2 px-3 text-[8px] font-black text-zinc-600 uppercase tracking-widest mt-4">BED CONFIG</div>
          <NavButton tab="sizes" label="AFMETINGEN" icon="📏" />
          <NavButton tab="colors" label="KLEUREN" icon="🎨" />
          <NavButton tab="headboards" label="HOOFDBORDEN" icon="🛌" />
          <NavButton tab="bases" label="BODEMS" icon="📦" />
          <NavButton tab="mattresses" label="MATRASSEN" icon="🛏️" />
          <NavButton tab="toppers" label="TOPPERS" icon="☁️" />
          <NavButton tab="poets" label="POTEN" icon="🦶" />
          <NavButton tab="voetbords" label="VOETBORDEN" icon="🛑" />
          <NavButton tab="voets" label="BANKJES" icon="🛋️" />
          <NavButton tab="nightstands" label="NACHTKASTJES" icon="🌙" />
          <NavButton tab="company" label="BEDRIJF" icon="🏢" />
        </div>

        <button onClick={onClose} className="mt-8 bg-zinc-900 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-zinc-800 transition-all">SLUITEN</button>
      </aside>

      <main className="flex-1 lg:ml-[220px] p-8 lg:p-12 bg-white min-h-screen">
        <div className="max-w-5xl mx-auto">
          {activeTab === 'dashboard' && (
             <div className="space-y-8">
                <div className="flex justify-between items-center border-b pb-4">
                  <h3 className="text-xl font-bold">Collecties</h3>
                  <button onClick={() => {
                    const newId = `config-${Date.now()}`;
                    onUpdateConfigs(prev => [...prev, { ...INITIAL_CONFIG, id: newId, name: 'Nieuwe Collectie' }]);
                    onSetActiveId(newId);
                  }} className="bg-black text-white px-4 py-2 rounded-lg text-xs font-bold">+ NIEUW BED</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {configs.map(config => (
                    <div key={config.id} className={`p-6 rounded-2xl border transition-all cursor-pointer ${activeId === config.id ? 'border-black bg-zinc-50' : 'border-zinc-100 hover:border-zinc-300'}`} onClick={() => onSetActiveId(config.id)}>
                      <input 
                        value={config.name} 
                        onChange={e => {
                          onUpdateConfigs(prev => prev.map(c => c.id === config.id ? { ...c, name: e.target.value } : c));
                          setHasUnsavedChanges(true);
                        }}
                        className="font-bold text-lg bg-transparent w-full mb-2 outline-none"
                      />
                      <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest">ID: {config.id}</p>
                    </div>
                  ))}
                </div>
             </div>
          )}

          {activeTab === 'sizes' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center border-b pb-4">
                <h3 className="text-xl font-bold uppercase tracking-widest">Afmetingen</h3>
                <button onClick={() => handleAddSubItem('sizes')} className="bg-black text-white px-4 py-2 rounded-lg text-xs font-bold">+ TOEVOEGEN</button>
              </div>
              <div className="space-y-3">
                {currentConfig.sizes.map((s, idx) => (
                  <div key={s.id} className="flex items-center gap-4 p-4 border border-zinc-100 rounded-xl bg-zinc-50/50">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => moveItem('sizes', idx, 'up')} disabled={idx === 0} className="text-zinc-400 hover:text-black disabled:opacity-30">▲</button>
                      <button onClick={() => moveItem('sizes', idx, 'down')} disabled={idx === currentConfig.sizes.length - 1} className="text-zinc-400 hover:text-black disabled:opacity-30">▼</button>
                    </div>
                    <input value={s.label} onChange={e => handleUpdateField('sizes', s.id, 'label', e.target.value)} className="font-bold bg-white px-3 py-1.5 border rounded w-32" />
                    <div className="flex items-center gap-2 text-xs font-bold text-zinc-400">
                      MOD: € <input type="number" value={s.priceModifier} onChange={e => handleUpdateField('sizes', s.id, 'priceModifier', Number(e.target.value))} className="w-20 border rounded px-2 py-1 text-black" />
                    </div>
                    <button onClick={() => handleRemoveSubItem('sizes', s.id)} className="ml-auto text-red-500 p-2 hover:bg-red-50 rounded-lg">🗑️</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab !== 'dashboard' && activeTab !== 'company' && activeTab !== 'sizes' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex justify-between items-center border-b pb-4">
                <h3 className="text-xl font-bold uppercase tracking-widest">{activeTab.toUpperCase()}</h3>
                <button onClick={() => handleAddSubItem(activeTab)} className="bg-black text-white px-4 py-2 rounded-lg text-xs font-bold">+ {activeTab.toUpperCase()} TOEVOEGEN</button>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {(currentConfig[activeTab] as any[]).map((item, idx) => (
                  <div key={item.id} className="p-6 border border-zinc-100 rounded-2xl bg-white shadow-sm flex flex-col md:flex-row gap-6">
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex gap-2">
                        <ImageBox label="DIAGONAAL" url={item.imageUrl} onFileChange={(f: File) => handleFileUpload(activeTab, item.id, 'imageUrl', f)} />
                        <ImageBox label="FRONT" url={item.imageUrlFront} onFileChange={(f: File) => handleFileUpload(activeTab, item.id, 'imageUrlFront', f)} />
                      </div>
                      <div className="flex gap-2 w-full">
                        <button onClick={() => moveItem(activeTab, idx, 'up')} disabled={idx === 0} className="flex-1 bg-zinc-100 py-1.5 rounded text-[10px] font-bold hover:bg-zinc-200 disabled:opacity-30">▲</button>
                        <button onClick={() => moveItem(activeTab, idx, 'down')} disabled={idx === (currentConfig[activeTab] as any[]).length - 1} className="flex-1 bg-zinc-100 py-1.5 rounded text-[10px] font-bold hover:bg-zinc-200 disabled:opacity-30">▼</button>
                      </div>
                    </div>
                    <div className="flex-1 space-y-4">
                      <div className="flex justify-between items-start">
                        <input value={item.name} onChange={e => handleUpdateField(activeTab, item.id, 'name', e.target.value)} className="font-bold text-lg w-full outline-none border-b focus:border-black transition-colors" />
                        <button onClick={() => handleRemoveSubItem(activeTab, item.id)} className="text-red-400 p-2 hover:bg-red-50 rounded-xl">🗑️</button>
                      </div>
                      <div className="flex items-center gap-2 text-xs font-bold text-zinc-400">
                        MEERPRIJS: € <input type="number" value={item.priceModifier} onChange={e => handleUpdateField(activeTab, item.id, 'priceModifier', Number(e.target.value))} className="w-24 border rounded px-2 py-1 text-black" />
                      </div>

                      {activeTab === 'headboards' && item.variants && (
                        <div className="mt-4 pt-4 border-t grid grid-cols-2 lg:grid-cols-4 gap-4">
                          {item.variants.map((v: any) => (
                            <div key={v.id} className="p-3 bg-zinc-50 rounded-xl border border-zinc-100 flex flex-col items-center gap-2">
                              <p className="text-[10px] font-black">{v.label}</p>
                              <div className="flex gap-1 scale-75">
                                <ImageBox label="D" url={v.imageUrl} size="w-16 h-16" onFileChange={(f: File) => handleFileUpload(activeTab, item.id, 'imageUrl', f, v.id)} />
                                <ImageBox label="F" url={v.imageUrlFront} size="w-16 h-16" onFileChange={(f: File) => handleFileUpload(activeTab, item.id, 'imageUrlFront', f, v.id)} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'company' && (
            <div className="max-w-2xl space-y-8">
              <h3 className="text-xl font-bold border-b pb-4">Bedrijfsinformatie</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ImageBox label="Bedrijfslogo" url={companyInfo.logoUrl} size="w-full h-32" onFileChange={(f: File) => handleFileUpload('company', '', 'logoUrl', f)} />
                <div className="space-y-4">
                  <InputGroup label="BEDRIJFSNAAM" value={companyInfo.companyName} onChange={v => onUpdateCompanyInfo({...companyInfo, companyName: v})} />
                  <InputGroup label="VERANTWOORDELIJKE" value={companyInfo.authorizedPerson} onChange={v => onUpdateCompanyInfo({...companyInfo, authorizedPerson: v})} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup label="TELEFOON" value={companyInfo.phone} onChange={v => onUpdateCompanyInfo({...companyInfo, phone: v})} />
                <InputGroup label="EMAIL" value={companyInfo.email} onChange={v => onUpdateCompanyInfo({...companyInfo, email: v})} />
              </div>
              <InputGroup label="ADRES" value={companyInfo.address} onChange={v => onUpdateCompanyInfo({...companyInfo, address: v})} />
              <InputGroup label="WEBSITE" value={companyInfo.website} onChange={v => onUpdateCompanyInfo({...companyInfo, website: v})} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const ImageBox = ({ label, url, onFileChange, size = "w-24 h-24" }: any) => (
  <div className="text-center group">
    <div className={`${size} bg-zinc-50 rounded-xl border-2 border-dashed border-zinc-200 flex items-center justify-center relative overflow-hidden transition-all group-hover:border-black cursor-pointer`}>
      {url ? (
        <img src={url} className="w-full h-full object-contain p-1" draggable={false} />
      ) : (
        <span className="text-[18px] opacity-20">📁</span>
      )}
      <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={e => e.target.files && onFileChange(e.target.files[0])} />
    </div>
    <p className="text-[8px] font-black text-zinc-400 uppercase tracking-widest mt-1.5">{label}</p>
  </div>
);

const InputGroup = ({ label, value, onChange }: { label: string, value: string, onChange: (v: string) => void }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">{label}</label>
    <input 
      value={value} 
      onChange={e => onChange(e.target.value)} 
      className="px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl outline-none focus:border-black transition-colors font-bold text-sm" 
    />
  </div>
);

export default AdminPanel;
